import React, { useState, useMemo, useCallback, useEffect } from "react";
import { ChevronDown, ChevronRight, RefreshCw, Search, Calendar, Users, FileText, Target, BarChart3, DollarSign, Package, TrendingUp, PieChart, Loader2 } from "lucide-react";
import api from "../api/client";


const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const typeMultipliers = {
  "CashSale": -1,
  "CustInvc": -1,
  "CustCred": 1,
  "CashRefund": 1,
  "Opprtnty": 0
};

const useBudgetMap = (budgetData) => {
  return useMemo(() => {
    const map = new Map();

    budgetData.forEach(b => {
      const d = new Date(b.month);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

      const key = `${monthKey}|${b.sales_rep}|${b.item_name}`;

      map.set(key, {
        amount: parseFloat(b.budgeted_amount) || 0,
        quantity: parseFloat(b.budgeted_quantity) || 0
      });
    });

    return map;
  }, [budgetData]);
};


const getMonthsInRange = (start, end) => {
  const result = [];
  const s = new Date(start), e = new Date(end);
  const d = new Date(s.getFullYear(), s.getMonth(), 1);
  while (d <= e) {
    result.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() + 1);
  }
  return result;
};

const getItemBudgetSummary = (selectedRep, start, end, budgetMap) => {
  const months = getMonthsInRange(start, end);
  const itemBudgets = {};
  for (const [key, { amount, quantity }] of budgetMap.entries()) {
    const [month, salesRep, itemName] = key.split("|");
    if ((selectedRep === "ALL" || salesRep === selectedRep) && months.includes(month)) {
      if (!itemBudgets[itemName]) itemBudgets[itemName] = { amount: 0, quantity: 0 };
      itemBudgets[itemName].amount += amount;
      itemBudgets[itemName].quantity += quantity;
    }
  }
  return itemBudgets;
};

const getMonthYear = dateStr => {
  const d = new Date(dateStr);
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
};

const formatCurrency = v =>
  v == null || isNaN(v) ? '' : '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2 });

const getMonthlyData = data => {
  const acc = {};
  data.forEach(i => {
    const key = getMonthYear(i.Date);
    const multiplier = typeMultipliers[i.Type] ?? 0;
    const revenue = parseFloat(i["Net Amount"]) || 0;
    const quantity = parseFloat(i.Quantity) || 0;

    if (!acc[key]) acc[key] = { revenue: 0, quantity: 0 };
    acc[key].revenue += revenue * multiplier;
    acc[key].quantity += quantity * multiplier;
  });

  return Object.entries(acc).sort((a, b) => {
    const dateA = new Date(a[0]);
    const dateB = new Date(b[0]);
    return dateA - dateB;
  });
};

const groupDataByCategory = data => {
  const grouped = {};
  data.forEach(d => {
    const category = d.Category || "Unknown";
    const subCategory = d["Sub Category"] || "Unknown";

    grouped[category] = grouped[category] || {};
    grouped[category][subCategory] = grouped[category][subCategory] || [];
    grouped[category][subCategory].push(d);
  });
  return grouped;
};

const sumRevenueQty = items => {
  return items.reduce(
    (a, c) => {
      const multiplier = typeMultipliers[c.Type] ?? 0;
      const revenue = parseFloat(c["Net Amount"]) || 0;
      const quantity = parseFloat(c.Quantity) || 0;
      const prob = parseFloat(c.Probability) || 0;

      a.total_revenue += revenue * multiplier;
      a.total_quantity_sold += quantity * multiplier;

      if (c.Type === "Opprtnty") {
        a.opportunity_amt += Math.abs(revenue);
        a.opportunity_qty += Math.abs(quantity);
        a.weighted_prob_total += prob * Math.abs(quantity);
        a.weighted_opp_amt += (Math.abs(revenue) * prob) / 100;
        a.weighted_opp_qty += (Math.abs(quantity) * prob) / 100;
      }

      return a;
    },
    {
      total_revenue: 0,
      total_quantity_sold: 0,
      opportunity_amt: 0,
      opportunity_qty: 0,
      weighted_prob_total: 0,
      weighted_opp_amt: 0,
      weighted_opp_qty: 0
    }
  );
};

const getSubCategoryItemSummary = items => {
  const m = {};
  items.forEach(i => {
    const multiplier = typeMultipliers[i.Type] ?? 0;
    const revenue = parseFloat(i["Net Amount"]) || 0;
    const quantity = parseFloat(i.Quantity) || 0;
    const prob = parseFloat(i.Probability) || 0;
    const itemName = i.ITEM || "Unknown";

    const item = m[itemName] = m[itemName] || {
      total_revenue: 0,
      total_quantity_sold: 0,
      opportunity_amt: 0,
      opportunity_qty: 0,
      weighted_prob_total: 0,
      weighted_opp_amt: 0,
      weighted_opp_qty: 0
    };

    item.total_revenue += revenue * multiplier;
    item.total_quantity_sold += quantity * multiplier;

    if (i.Type === "Opprtnty") {
      item.opportunity_amt += Math.abs(revenue);
      item.opportunity_qty += Math.abs(quantity);
      item.weighted_prob_total += prob * Math.abs(quantity);
      item.weighted_opp_amt += (Math.abs(revenue) * prob) / 100;
      item.weighted_opp_qty += (Math.abs(quantity) * prob) / 100;
    }
  });

  return Object.entries(m).map(([item_name, d]) => ({
    item_name,
    ...d,
    weighted_probability: d.opportunity_qty > 0
      ? d.weighted_prob_total / d.opportunity_qty
      : 0
  }));
};

export default function AnalyticsDashboard() {
  // API data state
  const [apiData, setApiData] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [startPicker, setStartPicker] = useState("2024-01-01");
  const [endPicker, setEndPicker] = useState("2024-12-31");
  const [repPicker, setRepPicker] = useState("ALL");
  const [typePicker, setTypePicker] = useState("ALL");
  const [catPicker, setCatPicker] = useState("ALL");
  const [subCatPicker, setSubCatPicker] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  const [activeStart, setActiveStart] = useState(startPicker);
  const [activeEnd, setActiveEnd] = useState(endPicker);
  const [activeRep, setActiveRep] = useState(repPicker);
  const [activeType, setActiveType] = useState(typePicker);
  const [activeCat, setActiveCat] = useState(catPicker);
  const [activeSubCat, setActiveSubCat] = useState(subCatPicker);
  const [activeSearch, setActiveSearch] = useState("");

  // Fetch data from API on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch transaction data with opportunities
        const transactionResponse = await api.get('/api/getJsonFromS32024Opp');
        const transactionJson = transactionResponse.data;
        const transactionArray = Array.isArray(transactionJson)
          ? transactionJson
          : (transactionJson.rows || []);

        // Fetch budget data
        const budgetResponse = await api.get('/api/getCombinedBudgetS32024');
        const budgetJson = budgetResponse.data;
        const budgetArray = Array.isArray(budgetJson)
          ? budgetJson
          : (budgetJson.rows || []);


        setApiData(transactionArray);
        setBudgetData(budgetArray);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const applyFilter = () => {
    setActiveStart(startPicker);
    setActiveEnd(endPicker);
    setActiveRep(repPicker);
    setActiveType(typePicker);
    setActiveCat(catPicker);
    setActiveSubCat(subCatPicker);
    setActiveSearch(searchQuery.trim().toLowerCase());
  };

  const budgetMap = useBudgetMap(budgetData);

  const categories = useMemo(() => {
    if (!apiData.length) return ["ALL"];
    return ["ALL", ...Array.from(new Set(apiData.map(i => i.Category).filter(Boolean)))];
  }, [apiData]);

  const subCategories = useMemo(() => {
    if (!apiData.length) return ["ALL"];
    return ["ALL", ...Array.from(new Set(apiData.map(i => i["Sub Category"]).filter(Boolean)))];
  }, [apiData]);

  const salesReps = useMemo(() => {
    if (!apiData.length) return ["ALL"];
    return ["ALL", ...Array.from(new Set(apiData.map(i => i["Sales Rep"]).filter(Boolean)))];
  }, [apiData]);

  const saleTypes = useMemo(() => {
    if (!apiData.length) return ["ALL"];
    return ["ALL", ...Array.from(new Set(apiData.map(i => i.Type).filter(Boolean)))];
  }, [apiData]);

  const filteredData = useMemo(() => {
    if (!apiData.length) return [];

    const s = new Date(activeStart), e = new Date(activeEnd);
    return apiData.filter(i => {
      const d = new Date(i.Date);
      const matchDate = d >= s && d <= e;
      const matchRep = activeRep === "ALL" || i["Sales Rep"] === activeRep;
      const matchType = activeType === "ALL" || i.Type === activeType;
      const matchCat = activeCat === "ALL" || i.Category === activeCat;
      const matchSubCat = activeSubCat === "ALL" || i["Sub Category"] === activeSubCat;
      const matchSearch =
        activeSearch === "" ||
        i.Customer?.toLowerCase().includes(activeSearch) ||
        i.Category?.toLowerCase().includes(activeSearch) ||
        i["Sub Category"]?.toLowerCase().includes(activeSearch) ||
        i.ITEM?.toLowerCase().includes(activeSearch);

      return matchDate && matchRep && matchType && matchCat && matchSubCat && matchSearch;
    });
  }, [apiData, activeStart, activeEnd, activeRep, activeType, activeCat, activeSubCat, activeSearch]);

  const itemBudgetMap = useMemo(() => getItemBudgetSummary(activeRep, activeStart, activeEnd, budgetMap), [activeRep, activeStart, activeEnd, budgetMap]);
  const monthlyData = useMemo(() => getMonthlyData(filteredData), [filteredData]);

  const monthlyTotals = useMemo(() => {
    return monthlyData.reduce((acc, [_, d]) => {
      acc.revenue += d.revenue;
      acc.qty += d.quantity;
      return acc;
    }, { revenue: 0, qty: 0 });
  }, [monthlyData]);

  const groupedData = useMemo(() => groupDataByCategory(filteredData), [filteredData]);

  const grandTotals = useMemo(() => {
    const itemNamesSeen = new Set();

    return filteredData.reduce((acc, item) => {
      const multiplier = typeMultipliers[item.Type] ?? 0;
      const revenue = parseFloat(item["Net Amount"]) || 0;
      const quantity = parseFloat(item.Quantity) || 0;
      const prob = parseFloat(item.Probability) || 0;

      acc.revenue += revenue * multiplier;
      acc.qty += quantity * multiplier;

      if (item.Type === "Opprtnty") {
        acc.oppAmt += Math.abs(revenue);
        acc.oppQty += Math.abs(quantity);
        acc.wtdProb += prob * Math.abs(quantity);
        acc.wtdOppAmt += (Math.abs(revenue) * prob) / 100;
      }

      // Only add budget once per unique item
      if (!itemNamesSeen.has(item.ITEM)) {
        acc.budgetAmt += itemBudgetMap[item.ITEM]?.amount || 0;
        itemNamesSeen.add(item.ITEM);
      }

      return acc;
    }, {
      revenue: 0,
      qty: 0,
      budgetAmt: 0,
      oppAmt: 0,
      oppQty: 0,
      wtdProb: 0,
      wtdOppAmt: 0
    });
  }, [filteredData, itemBudgetMap]);

  const grandTotalsQty = useMemo(() => {
    const itemNamesSeen = new Set();

    return filteredData.reduce((acc, item) => {
      const multiplier = typeMultipliers[item.Type] ?? 0;
      const revenue = parseFloat(item["Net Amount"]) || 0;
      const quantity = parseFloat(item.Quantity) || 0;
      const prob = parseFloat(item.Probability) || 0;

      acc.revenue += revenue * multiplier;
      acc.qty += quantity * multiplier;

      if (item.Type === "Opprtnty") {
        acc.oppAmt += Math.abs(revenue);
        acc.oppQty += Math.abs(quantity);
        acc.wtdProb += prob * Math.abs(quantity);
        acc.wtdOppQty += (Math.abs(quantity) * prob) / 100;
      }

      // Only add budget once per unique item
      if (!itemNamesSeen.has(item.ITEM)) {
        acc.budgetQty += itemBudgetMap[item.ITEM]?.quantity || 0;
        itemNamesSeen.add(item.ITEM);
      }

      return acc;
    }, {
      revenue: 0,
      qty: 0,
      budgetQty: 0,
      oppAmt: 0,
      oppQty: 0,
      wtdProb: 0,
      wtdOppQty: 0
    });
  }, [filteredData, itemBudgetMap]);

  const [openCategories1, setOpenCategories1] = useState({});
  const [openSubCategories1, setOpenSubCategories1] = useState({});
  const [openCategories2, setOpenCategories2] = useState({});
  const [openSubCategories2, setOpenSubCategories2] = useState({});

  const toggleCategory1 = cat => setOpenCategories1(p => ({ ...p, [cat]: !p[cat] }));
  const toggleSub1 = (cat, sub) => setOpenSubCategories1(p => ({ ...p, [`${cat}-${sub}`]: !p[`${cat}-${sub}`] }));
  const toggleCategory2 = cat => setOpenCategories2(p => ({ ...p, [cat]: !p[cat] }));
  const toggleSub2 = (cat, sub) => setOpenSubCategories2(p => ({ ...p, [`${cat}-${sub}`]: !p[`${cat}-${sub}`] }));

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 text-indigo-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Data...</h2>
          <p className="text-gray-600">Fetching transaction and budget data from server</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 max-w-md">
          <div className="text-center">
            <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-600 text-3xl">⚠</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-full mx-auto px-6 py-8">
        {/* Enhanced Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Budget Analysis</h1>
              <p className="text-gray-600 text-lg">Sales, budget and opportunity comprehensive analysis</p>
            </div>
            <div className="flex items-center space-x-6">
              <div className="text-right">
                <p className="text-sm text-gray-500">Total Records</p>
                <p className="text-2xl font-bold text-indigo-600">{filteredData.length.toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Budget Coverage</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(grandTotals.budgetAmt)}</p>
              </div>
            </div>
          </div>

          {/* Enhanced Filter Bar */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Search Bar */}
              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Search className="inline h-4 w-4 mr-2" />
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              </div>

              {/* Date Range */}
              <div className="lg:col-span-1 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Calendar className="inline h-4 w-4 mr-2" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startPicker}
                    onChange={e => setStartPicker(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={endPicker}
                    onChange={e => setEndPicker(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                  />
                </div>
              </div>

              {/* Sales Rep & Type */}
              <div className="lg:col-span-1 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Users className="inline h-4 w-4 mr-2" />
                    Sales Rep
                  </label>
                  <select
                    value={repPicker}
                    onChange={e => setRepPicker(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                  >
                    {salesReps.map(rep => <option key={rep} value={rep}>{rep}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FileText className="inline h-4 w-4 mr-2" />
                    Type
                  </label>
                  <select
                    value={typePicker}
                    onChange={e => setTypePicker(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                  >
                    {saleTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {/* Categories & Apply Button */}
              <div className="lg:col-span-1 grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Target className="inline h-4 w-4 mr-2" />
                    Category
                  </label>
                  <select
                    value={catPicker}
                    onChange={e => setCatPicker(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                  >
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Sub Category</label>
                  <select
                    value={subCatPicker}
                    onChange={e => setSubCatPicker(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
                  >
                    {subCategories.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={applyFilter}
                    className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 text-sm"
                  >
                    <RefreshCw size={16} />
                    <span>Apply Filters</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-12 gap-8 min-h-[calc(100vh-280px)]">
          {/* Monthly Statistics Cards */}
          <div className="col-span-3 space-y-6">
            {/* Monthly Revenue Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <DollarSign className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Monthly Revenue</h2>
                    <p className="text-emerald-100 text-sm">Track revenue trends</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="overflow-y-auto max-h-96 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0 rounded-lg">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700 rounded-tl-lg">Month</th>
                        <th className="text-right px-4 py-3 font-semibold text-gray-700 rounded-tr-lg">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {monthlyData.map(([m, d], i) => (
                        <tr key={m} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-4 py-3 font-medium text-gray-800">{m}</td>
                          <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">
                            ${d.revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-800">Total Revenue</span>
                    <span className="text-xl font-bold text-emerald-600">
                      ${monthlyTotals.revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly Quantity Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Package className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Monthly Quantity</h2>
                    <p className="text-blue-100 text-sm">Units sold over time</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="overflow-y-auto max-h-96 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0 rounded-lg">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700 rounded-tl-lg">Month</th>
                        <th className="text-right px-4 py-3 font-semibold text-gray-700 rounded-tr-lg">Quantity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {monthlyData.map(([m, d], i) => (
                        <tr key={m} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-4 py-3 font-medium text-gray-800">{m}</td>
                          <td className="px-4 py-3 text-right font-mono font-semibold text-blue-600">{d.quantity.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-800">Total Quantity</span>
                    <span className="text-xl font-bold text-blue-600">
                      {monthlyTotals.qty.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Analysis Tables */}
          <div className="col-span-9 space-y-6">
            {/* Revenue Analysis Table - Continue with full table code from your original Page 2 */}
            {/* I'll include the complete table structures here */}

            {/* Revenue Analysis Table */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <BarChart3 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Revenue & Budget Analysis</h2>
                    <p className="text-purple-100 text-sm">Sales vs budget performance with opportunities</p>
                  </div>
                </div>
              </div>
              <div className="overflow-auto max-h-[500px]">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold text-gray-700">Category / Sub</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Total Revenue</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Budget Amount</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Opportunity Amount</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Probability %</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Wtd. Opportunity</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Variance</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Quantity Sold</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {Object.entries(groupedData).map(([cat, subs]) => {
                      const allItems = Object.values(subs).flat();
                      const catSum = sumRevenueQty(allItems);
                      const uniqueItems = [...new Set(allItems.map(i => i.ITEM))];
                      const catBudgetAmt = uniqueItems.reduce((acc, item) => acc + (itemBudgetMap[item]?.amount || 0), 0);
                      const catKey = cat;

                      return (
                        <React.Fragment key={cat}>
                          <tr
                            onClick={() => toggleCategory1(cat)}
                            className="cursor-pointer bg-gradient-to-r from-purple-50 to-purple-25 hover:from-purple-100 hover:to-purple-50 transition-all duration-200 border-l-4 border-purple-400"
                          >
                            <td className="px-6 py-4 flex items-center font-semibold text-gray-800">
                              <span className="mr-3 text-purple-600 transition-transform duration-200">
                                {openCategories1[catKey] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              </span>
                              {cat}
                            </td>
                            <td className="px-4 py-4 text-right font-mono font-semibold text-emerald-600">
                              ${catSum.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-4 text-right font-mono font-medium text-gray-600">{formatCurrency(catBudgetAmt)}</td>
                            <td className="px-4 py-4 text-right font-mono font-medium text-blue-600">{formatCurrency(catSum.opportunity_amt)}</td>
                            <td className="px-4 py-4 text-right font-mono font-medium text-amber-600">
                              {catSum.opportunity_qty > 0
                                ? (catSum.weighted_prob_total / catSum.opportunity_qty).toFixed(1) + "%"
                                : ""}
                            </td>
                            <td className="px-4 py-4 text-right font-mono font-medium text-indigo-600">
                              {formatCurrency(catSum.weighted_opp_amt)}
                            </td>
                            <td className="px-4 py-4 text-right font-mono font-medium text-red-600">
                              {formatCurrency(catSum.total_revenue - (catBudgetAmt || 0) - catSum.weighted_opp_amt)}
                            </td>
                            <td className="px-4 py-4 text-right font-mono font-medium text-gray-700">{catSum.total_quantity_sold.toLocaleString()}</td>
                          </tr>

                          {openCategories1[catKey] && Object.entries(subs).map(([sub, items]) => {
                            const subKey = `${cat}-${sub}`;
                            const subSum = sumRevenueQty(items);
                            const uniqueSubItems = [...new Set(items.map(i => i.ITEM))];
                            const subBudgetAmt = uniqueSubItems.reduce((acc, item) => acc + (itemBudgetMap[item]?.amount || 0), 0);
                            const aggItems = getSubCategoryItemSummary(items);

                            return (
                              <React.Fragment key={sub}>
                                <tr
                                  onClick={() => toggleSub1(cat, sub)}
                                  className="cursor-pointer bg-gradient-to-r from-amber-50 to-amber-25 hover:from-amber-100 hover:to-amber-50 transition-all duration-200 border-l-4 border-amber-400"
                                >
                                  <td className="px-6 py-3 pl-12 flex items-center font-medium text-gray-700">
                                    <span className="mr-3 text-amber-600 transition-transform duration-200">
                                      {openSubCategories1[subKey] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                    </span>
                                    {sub}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-emerald-600">
                                    ${subSum.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-gray-600">{formatCurrency(subBudgetAmt)}</td>
                                  <td className="px-4 py-3 text-right font-mono text-blue-600">{formatCurrency(subSum.opportunity_amt)}</td>
                                  <td className="px-4 py-3 text-right font-mono text-amber-600">
                                    {subSum.opportunity_qty > 0
                                      ? (subSum.weighted_prob_total / subSum.opportunity_qty).toFixed(1) + "%"
                                      : ""}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-indigo-600">
                                    {formatCurrency(subSum.weighted_opp_amt)}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-red-600">
                                    {formatCurrency(subSum.total_revenue - (subBudgetAmt || 0) - subSum.weighted_opp_amt)}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-gray-700">{subSum.total_quantity_sold.toLocaleString()}</td>
                                </tr>

                                {openSubCategories1[subKey] && aggItems.map((it, i) => (
                                  <tr key={it.item_name + i} className="bg-white hover:bg-gray-50 transition-colors duration-150">
                                    <td className="px-6 py-3 pl-16 text-gray-600">{it.item_name}</td>
                                    <td className="px-4 py-3 text-right font-mono text-emerald-600">
                                      ${it.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-gray-600">{formatCurrency(itemBudgetMap[it.item_name]?.amount)}</td>
                                    <td className="px-4 py-3 text-right font-mono text-blue-600">{formatCurrency(it.opportunity_amt)}</td>
                                    <td className="px-4 py-3 text-right font-mono text-amber-600">
                                      {it.opportunity_qty > 0
                                        ? (it.weighted_probability).toFixed(1) + "%"
                                        : ""}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-indigo-600">
                                      {formatCurrency(it.weighted_opp_amt)}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-red-600">
                                      {formatCurrency(it.total_revenue - ((itemBudgetMap[it.item_name]?.amount) || 0) - it.weighted_opp_amt)}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-gray-600">{it.total_quantity_sold.toLocaleString()}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                <div className="sticky bottom-0 bg-gradient-to-r from-purple-100 to-purple-50 border-t-2 border-purple-300">
                  <div className="px-6 py-4 grid grid-cols-8 gap-4 font-bold text-gray-800 text-sm">
                    <span>TOTAL</span>
                    <span className="text-right text-purple-700">{formatCurrency(grandTotals.revenue)}</span>
                    <span className="text-right text-purple-700">{formatCurrency(grandTotals.budgetAmt)}</span>
                    <span className="text-right text-purple-700">{formatCurrency(grandTotals.oppAmt)}</span>
                    <span className="text-right text-purple-700">
                      {grandTotals.oppQty > 0
                        ? (grandTotals.wtdProb / grandTotals.oppQty).toFixed(1) + "%"
                        : ""}
                    </span>
                    <span className="text-right text-purple-700">{formatCurrency(grandTotals.wtdOppAmt)}</span>
                    <span className="text-right text-purple-700">
                      {formatCurrency((grandTotals.budgetAmt || 0) - grandTotals.revenue - grandTotals.wtdOppAmt)}
                    </span>
                    <span className="text-right text-purple-700">{grandTotals.qty.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quantity Analysis Table */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="bg-gradient-to-r from-pink-500 to-pink-600 px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <PieChart className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Quantity & Budget Analysis</h2>
                    <p className="text-pink-100 text-sm">Unit quantities vs budget forecasts</p>
                  </div>
                </div>
              </div>
              <div className="overflow-auto max-h-[500px]">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold text-gray-700">Category / Sub</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Quantity Sold</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Budget Quantity</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Opportunity Qty</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Probability %</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Wtd. Opportunity</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Variance</th>
                      <th className="px-4 py-4 text-right font-semibold text-gray-700">Total Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {Object.entries(groupedData).map(([cat, subs]) => {
                      const allItems = Object.values(subs).flat();
                      const catSum = sumRevenueQty(allItems);
                      const uniqueItems = [...new Set(allItems.map(i => i.ITEM))];
                      const catBudgetQty = uniqueItems.reduce((acc, item) => acc + (itemBudgetMap[item]?.quantity || 0), 0);
                      const catKey = cat;

                      return (
                        <React.Fragment key={cat}>
                          <tr
                            onClick={() => toggleCategory2(cat)}
                            className="cursor-pointer bg-gradient-to-r from-pink-50 to-pink-25 hover:from-pink-100 hover:to-pink-50 transition-all duration-200 border-l-4 border-pink-400"
                          >
                            <td className="px-6 py-4 flex items-center font-semibold text-gray-800">
                              <span className="mr-3 text-pink-600 transition-transform duration-200">
                                {openCategories2[catKey] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              </span>
                              {cat}
                            </td>
                            <td className="px-4 py-4 text-right font-mono font-semibold text-gray-700">{catSum.total_quantity_sold.toLocaleString()}</td>
                            <td className="px-4 py-4 text-right font-mono font-medium text-gray-600">{catBudgetQty.toLocaleString()}</td>
                            <td className="px-4 py-4 text-right font-mono font-medium text-blue-600">{catSum.opportunity_qty.toLocaleString()}</td>
                            <td className="px-4 py-4 text-right font-mono font-medium text-amber-600">
                              {catSum.opportunity_qty > 0
                                ? (catSum.weighted_prob_total / catSum.opportunity_qty).toFixed(1) + "%"
                                : ""}
                            </td>
                            <td className="px-4 py-4 text-right font-mono font-medium text-indigo-600">
                              {(catSum.weighted_opp_qty || 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-4 text-right font-mono font-medium text-red-600">
                              {(catSum.total_quantity_sold - (catBudgetQty || 0) - (catSum.weighted_opp_qty || 0)).toFixed(2)}
                            </td>
                            <td className="px-4 py-4 text-right font-mono font-semibold text-emerald-600">
                              ${catSum.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                          </tr>

                          {openCategories2[catKey] && Object.entries(subs).map(([sub, items]) => {
                            const subKey = `${cat}-${sub}`;
                            const subSum = sumRevenueQty(items);
                            const uniqueSubItems = [...new Set(items.map(i => i.ITEM))];
                            const subBudgetQty = uniqueSubItems.reduce((acc, item) => acc + (itemBudgetMap[item]?.quantity || 0), 0);
                            const aggItems = getSubCategoryItemSummary(items);

                            return (
                              <React.Fragment key={sub}>
                                <tr
                                  onClick={() => toggleSub2(cat, sub)}
                                  className="cursor-pointer bg-gradient-to-r from-amber-50 to-amber-25 hover:from-amber-100 hover:to-amber-50 transition-all duration-200 border-l-4 border-amber-400"
                                >
                                  <td className="px-6 py-3 pl-12 flex items-center font-medium text-gray-700">
                                    <span className="mr-3 text-amber-600 transition-transform duration-200">
                                      {openSubCategories2[subKey] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                    </span>
                                    {sub}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-gray-700">{subSum.total_quantity_sold.toLocaleString()}</td>
                                  <td className="px-4 py-3 text-right font-mono text-gray-600">{subBudgetQty.toLocaleString()}</td>
                                  <td className="px-4 py-3 text-right font-mono text-blue-600">{subSum.opportunity_qty.toLocaleString()}</td>
                                  <td className="px-4 py-3 text-right font-mono text-amber-600">
                                    {subSum.opportunity_qty > 0
                                      ? (subSum.weighted_prob_total / subSum.opportunity_qty).toFixed(1) + "%"
                                      : ""}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-indigo-600">
                                    {(subSum.weighted_opp_qty || 0).toFixed(2)}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-red-600">
                                    {(subSum.total_quantity_sold - (subBudgetQty || 0) - (subSum.weighted_opp_qty || 0)).toFixed(2)}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono text-emerald-600">
                                    ${subSum.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </td>
                                </tr>

                                {openSubCategories2[subKey] && aggItems.map((it, i) => (
                                  <tr key={it.item_name + i} className="bg-white hover:bg-gray-50 transition-colors duration-150">
                                    <td className="px-6 py-3 pl-16 text-gray-600">{it.item_name}</td>
                                    <td className="px-4 py-3 text-right font-mono text-gray-700">{it.total_quantity_sold.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right font-mono text-gray-600">{(itemBudgetMap[it.item_name]?.quantity || 0).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right font-mono text-blue-600">{it.opportunity_qty.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right font-mono text-amber-600">
                                      {it.opportunity_qty > 0
                                        ? (it.weighted_probability).toFixed(1) + "%"
                                        : ""}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-indigo-600">
                                      {(it.weighted_opp_qty || 0).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-red-600">
                                      {(it.total_quantity_sold - ((itemBudgetMap[it.item_name]?.quantity) || 0) - (it.weighted_opp_qty || 0)).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-emerald-600">
                                      ${it.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                <div className="sticky bottom-0 bg-gradient-to-r from-pink-100 to-pink-50 border-t-2 border-pink-300">
                  <div className="px-6 py-4 grid grid-cols-8 gap-4 font-bold text-gray-800 text-sm">
                    <span>TOTAL</span>
                    <span className="text-right text-pink-700">{grandTotalsQty.qty.toLocaleString()}</span>
                    <span className="text-right text-pink-700">{(grandTotalsQty.budgetQty).toLocaleString()}</span>
                    <span className="text-right text-pink-700">{(grandTotalsQty.oppQty).toLocaleString()}</span>
                    <span className="text-right text-pink-700">
                      {grandTotalsQty.oppQty > 0
                        ? (grandTotalsQty.wtdProb / grandTotalsQty.oppQty).toFixed(1) + "%"
                        : ""}
                    </span>
                    <span className="text-right text-pink-700">{(grandTotalsQty.wtdOppQty).toFixed(2)}</span>
                    <span className="text-right text-pink-700">
                      {(grandTotalsQty.qty - (grandTotalsQty.budgetQty || 0) - grandTotalsQty.wtdOppQty).toFixed(2)}
                    </span>
                    <span className="text-right text-pink-700">{formatCurrency(grandTotalsQty.revenue)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}